// Artifact mapper (ARCHITECTURE.md §7.4).
//
// Maps files to DSL artifacts using their `path_patterns` (glob). A file may
// belong to zero, one, or multiple artifacts. Matching is pure string-based
// (no filesystem access) via picomatch — the same engine `tinyglobby` builds
// on, so results stay consistent with the file-scanner's globbing (§6.6).

import picomatch from "picomatch";

/**
 * Return the ids of every artifact whose glob patterns match `filePath`.
 *
 * @param filePath        Project-relative path (matches the AST graph node ids).
 * @param artifactPatterns artifactId → glob patterns (the DSL `path_patterns`).
 * @returns               Matching artifact ids, sorted for deterministic output.
 *                        Artifacts with no patterns never match.
 */
export function mapFileToArtifacts(
  filePath: string,
  artifactPatterns: Map<string, string[]>,
): string[] {
  const matched: string[] = [];

  for (const [artifactId, patterns] of artifactPatterns) {
    if (patterns.length === 0) continue;
    if (picomatch.isMatch(filePath, patterns)) {
      matched.push(artifactId);
    }
  }

  matched.sort();
  return matched;
}

/**
 * Batch variant of {@link mapFileToArtifacts}: build a filePath → artifactId[]
 * mapping for many files at once. Every input file gets an entry (possibly an
 * empty array when it matches no artifact).
 */
export function buildArtifactMapping(
  files: string[],
  artifactPatterns: Map<string, string[]>,
): Map<string, string[]> {
  const mapping = new Map<string, string[]>();
  for (const filePath of files) {
    mapping.set(filePath, mapFileToArtifacts(filePath, artifactPatterns));
  }
  return mapping;
}
